'use client';

/**
 * PLATE.JS EDITOR COMPONENTS - Usage Guide
 *
 * These components wrap Plate.js to provide consistent styling and behavior.
 *
 * ARCHITECTURE:
 * =============
 * - EditorContainer provides outer styling (border, padding, background)
 * - Editor is "naked" by default (variant="none") - no padding/styling
 * - Container variants (formField, inline, document) apply all necessary styling
 * - Editor only needs explicit variant for standalone full-page use
 *
 * USAGE PATTERNS:
 * ===============
 *
 * 1. FORM FIELD (like TenantScreeningForm)
 * -----------------------------------------
 * Use variant="formField" on EditorContainer. Editor has no variant (defaults to "none").
 *
 * @example
 * <EditorContainer variant="formField">
 *   <Editor placeholder="Type here..." />
 * </EditorContainer>
 *
 * Features: Border, focus ring, form-like background, min-height 120px
 *
 * 2. INLINE COMMENTS/REPLIES (CommentForm, ReplyForm)
 * -----------------------------------------------------
 * Use variant="inline" on EditorContainer with height constraints.
 *
 * @example
 * <EditorContainer variant="inline" className="max-h-[150px] min-h-[60px]">
 *   <Editor placeholder="Add a comment..." />
 * </EditorContainer>
 *
 * Features: Borderless, dark mode support, flex layout, text-sm
 * Customize: Add className for specific height constraints
 *
 * 3. DOCUMENT EDITOR (DescriptionForm)
 * -------------------------------------
 * Use variant="document" on EditorContainer for editors with toolbars.
 * Add padding to Editor (not Container) so toolbar is flush to edges.
 *
 * @example
 * <EditorContainer variant="document" className="min-h-[400px]">
 *   <FixedToolbar className="border-b">...</FixedToolbar>
 *   <Editor className="px-3 py-1" placeholder="..." />
 * </EditorContainer>
 *
 * Features: Large height, toolbar support, secondary background
 * Note: Container has NO padding (toolbar flush), Editor has padding (px-3 py-1)
 *
 * TECHNICAL NOTES:
 * ================
 * - Editor defaults to variant="none" (no padding) - sensible for container usage
 * - Placeholder alignment fix: `[&_.slate-p]:py-0` removes paragraph padding
 * - EditorContainer variants provide all styling - Editor stays clean
 * - For standalone full-page editors, explicitly use Editor variant="default"
 *
 * STANDALONE VARIANTS (rarely used):
 * ===================================
 * - "default": Full-page editor with px-3 py-1 pb-8 (use when no container)
 * - "demo": Similar to default with px-3 py-1 pb-8
 * - "fullWidth": Full width with px-3 py-1 pb-8
 * - "comment": Legacy compact inline (use inline container variant instead)
 * - "none": No styling (default for editors inside containers)
 */

import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import type { PlateContentProps } from 'platejs/react';
import { PlateContainer, PlateContent } from 'platejs/react';
import * as React from 'react';

import { cn } from '@/lib/utils-client';

/**
 * EditorContainer Variants
 *
 * Controls the outer container styling for the editor.
 * Apply custom padding here when using variant="none" on Editor.
 */
const editorContainerVariants = cva(
  'relative w-full cursor-text overflow-y-auto caret-primary select-text selection:bg-brand/25 focus-visible:outline-none [&_.slate-selection-area]:z-50 [&_.slate-selection-area]:border [&_.slate-selection-area]:border-brand/25 [&_.slate-selection-area]:bg-brand/15',
  {
    defaultVariants: {
      variant: 'default',
    },
    variants: {
      variant: {
        // Legacy: Compact inline editor with focus states (for comments/replies)
        comment: cn(
          'flex flex-wrap justify-between gap-1 px-1 py-0.5 text-sm',
          'rounded-md border-[1.5px] border-transparent bg-transparent',
          'has-[[data-slate-editor]:focus]:border-brand/50 has-[[data-slate-editor]:focus]:ring-2 has-[[data-slate-editor]:focus]:ring-brand/30',
          'has-aria-disabled:border-input has-aria-disabled:bg-muted'
        ),
        // Full height container (pair with Editor variant="default")
        default: 'h-full',
        // Fixed height demo container
        demo: 'h-[650px]',
        // Document editor variant - for large editors with formatting toolbars
        // Note: No padding - toolbar should be flush to edges, padding goes on Editor
        document: cn(
          'overflow-hidden rounded-lg bg-secondary'
        ),
        // Form field variant - looks like a standard form input field
        formField: cn(
          'min-h-[120px] overflow-y-auto rounded-lg',
          'border border-input bg-background px-3 py-1',
          'ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
          '[&_.slate-editor]:!h-full' // Ensure editor fills container height
        ),
        // Inline variant - for borderless inline editors (comments/replies)
        inline: cn(
          'flex w-full overflow-y-auto rounded-lg',
          'border-none bg-secondary px-3 py-1 text-sm',
          'ring-0 outline-none focus:ring-0 focus:outline-none focus-visible:ring-0 focus-visible:outline-none',
          'dark:bg-input/30 dark:hover:bg-input/50',
          '[&_.slate-editor]:!h-full' // Ensure editor fills container height
        ),
        // Select-style container with border and focus ring
        select: cn(
          'group rounded-md border border-input ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
          'has-data-readonly:w-fit has-data-readonly:cursor-default has-data-readonly:border-transparent has-data-readonly:focus-within:[box-shadow:none]'
        ),
      },
    },
  }
);

export function EditorContainer({
  className,
  variant,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof editorContainerVariants>) {
  return (
    <PlateContainer
      className={cn(
        'ignore-click-outside/toolbar',
        editorContainerVariants({ variant }),
        className
      )}
      {...props}
    />
  );
}

/**
 * Editor Variants
 *
 * Controls the editor div styling and padding.
 *
 * DEFAULT: variant="none" (no padding) - sensible for use inside EditorContainer.
 * STANDALONE: Use variant="default" for full-page editors without a container.
 *
 * Note: The "default" variant has extensive padding (px-16 pt-4 pb-72) suitable
 * for standalone full-page editing, but causes issues when used inside containers.
 */
const editorVariants = cva(
  cn(
    'group/editor',
    'relative w-full cursor-text overflow-x-hidden break-words whitespace-pre-wrap select-text',
    'rounded-md ring-offset-background focus-visible:outline-none',
    'placeholder:text-muted-foreground/80 **:data-slate-placeholder:text-muted-foreground/80 **:data-slate-placeholder:opacity-100!',
    '[&_strong]:font-bold',
    '[&_.slate-p]:py-0' // Remove paragraph padding to align placeholder with cursor
  ),
  {
    defaultVariants: {
      variant: 'none',
    },
    variants: {
      disabled: {
        true: 'cursor-not-allowed opacity-50',
      },
      focused: {
        true: 'ring-2 ring-ring ring-offset-2',
      },
      variant: {
        // AI assistant style (no padding)
        ai: 'w-full px-0 text-base md:text-sm',
        // AI chat style with max height
        aiChat:
          'max-h-[min(70vh,320px)] w-full max-w-[700px] overflow-y-auto px-3 py-2 text-base md:text-sm',
        // Compact inline style (legacy, use inline container variant instead)
        comment: cn('rounded-none border-none bg-transparent text-sm'),
        // Full-page standalone editor (rarely used - prefer document container variant)
        default: 'size-full px-3 py-1 pb-8 text-base',
        // Demo variant for testing (rarely used)
        demo: 'size-full px-3 py-1 pb-8 text-base',
        // Full width standalone editor (rarely used)
        fullWidth: 'size-full px-3 py-1 pb-8 text-base',
        // NO STYLING - default for editors inside containers
        none: '',
        // Select-style input
        select: 'px-3 py-2 text-base data-readonly:w-fit',
      },
    },
  }
);

export type EditorProps = PlateContentProps &
  VariantProps<typeof editorVariants>;

export const Editor = React.forwardRef<HTMLDivElement, EditorProps>(
  ({ className, disabled, focused, variant, ...props }, ref) => {
    return (
      <PlateContent
        ref={ref}
        className={cn(
          editorVariants({
            disabled,
            focused,
            variant,
          }),
          className
        )}
        disabled={disabled}
        disableDefaultStyles
        {...props}
      />
    );
  }
);

Editor.displayName = 'Editor';
